// Conteo físico — new session / freeze-scope form (/inventory/conteo/nuevo).
//
// "Create" IS "freeze": submitting snapshots the live scope from paceAPIserv
// straight into the session's lines. A scope wider than the server's confirm
// threshold 409s with {error:"scope_too_large", count} — the form re-submits
// with confirm:true once the user accepts the ConfirmDialog.
import { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  Button, Card, Select, SelectItem, Switch, Text, TextInput, Title,
} from '@tremor/react';
import AccessDenied from '../components/AccessDenied';
import ConfirmDialog from '../components/ConfirmDialog';
import { createSession, ScopeTooLargeError, type CreateCountSessionBody } from '../api/inventoryCount';
import { getStores } from '../api/priceUpload';
import { canUseCount } from '../lib/roles';
import { t } from '../lib/i18n';
import type { Store, User } from '../api/types';

type ScopeType = 'line' | 'bin' | 'supplier' | 'all';
const SCOPE_TYPES: ScopeType[] = ['line', 'bin', 'supplier', 'all'];
type PostMode = 'delta' | 'overwrite';
type UncountedMode = 'skip' | 'zero';

export default function InventoryCountNew() {
  const user = useOutletContext<User>();
  if (!canUseCount(user)) return <AccessDenied user={user} />;
  return <InventoryCountNewInner user={user} />;
}

function InventoryCountNewInner({ user }: { user: User }) {
  const navigate = useNavigate();

  const [stores, setStores] = useState<Store[]>([]);
  const [storesErr, setStoresErr] = useState<string | null>(null);
  const [sid, setSid] = useState(0);
  const [name, setName] = useState('');
  const [scopeType, setScopeType] = useState<ScopeType>('line');
  const [scopeFrom, setScopeFrom] = useState('');
  const [scopeTo, setScopeTo] = useState('');
  const [classFilter, setClassFilter] = useState('*');
  const [postMode, setPostMode] = useState<PostMode>('delta');
  const [uncounted, setUncounted] = useState<UncountedMode>('skip');
  const [blind, setBlind] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmCount, setConfirmCount] = useState<number | null>(null);

  useEffect(() => {
    getStores()
      .then((list) => {
        setStores(list);
        setSid((prev) => prev || user.sid_assigned || list[0]?.sid || 0);
      })
      .catch((e: Error) => {
        setStoresErr(e.message ?? String(e));
        if (user.sid_assigned) setSid(user.sid_assigned);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const needsFrom = scopeType !== 'all';
  const needsTo = scopeType === 'line' || scopeType === 'bin';
  const canSubmit =
    !submitting && sid > 0 && name.trim() !== '' && (!needsFrom || scopeFrom.trim() !== '');

  async function submit(confirm: boolean) {
    setErr(null);
    setSubmitting(true);
    try {
      const body: CreateCountSessionBody = {
        sid,
        name: name.trim(),
        scope_type: scopeType,
        scope_from: needsFrom ? scopeFrom.trim() : '',
        scope_to: needsTo ? (scopeTo.trim() || scopeFrom.trim()) : '',
        class_filter: classFilter.trim() || '*',
        post_mode: postMode,
        uncounted,
        blind,
        confirm,
      };
      const session = await createSession(body);
      navigate(`/inventory/conteo/${session.id}`);
    } catch (e) {
      if (e instanceof ScopeTooLargeError) {
        setConfirmCount(e.count);
      } else {
        setErr((e as Error).message ?? t.conteo.errors.generic);
      }
    } finally {
      setSubmitting(false);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    void submit(false);
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <Title>{t.conteo.newSession}</Title>

      {storesErr && (
        <Text className="text-red-600 dark:text-red-400 text-sm">{storesErr}</Text>
      )}

      <Card>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="text-sm">
              <div className="text-tremor-content text-xs mb-1">{t.conteo.form.name}</div>
              <TextInput value={name} onValueChange={setName} placeholder="Conteo mensual — línea GON" required />
            </label>

            <label className="text-sm">
              <div className="text-tremor-content text-xs mb-1">{t.conteo.form.store}</div>
              {stores.length > 0 ? (
                <Select value={sid ? String(sid) : ''} onValueChange={(v) => setSid(Number(v) || 0)} enableClear={false}>
                  {stores.map((s) => (
                    <SelectItem key={s.sid} value={String(s.sid)}>{s.sid} — {s.name}</SelectItem>
                  ))}
                </Select>
              ) : (
                <Text className="text-sm text-tremor-content py-2">
                  {sid > 0 ? `#${sid}` : '—'}
                </Text>
              )}
            </label>

            <label className="text-sm">
              <div className="text-tremor-content text-xs mb-1">{t.conteo.form.scopeType}</div>
              <Select value={scopeType} onValueChange={(v) => setScopeType(v as ScopeType)} enableClear={false}>
                {SCOPE_TYPES.map((v) => (
                  <SelectItem key={v} value={v}>{t.conteo.form.scopeOptions[v]}</SelectItem>
                ))}
              </Select>
            </label>

            <label className="text-sm">
              <div className="text-tremor-content text-xs mb-1">{t.conteo.form.classFilter}</div>
              <TextInput value={classFilter} onValueChange={setClassFilter} placeholder="*" />
            </label>

            {needsFrom && (
              <label className="text-sm">
                <div className="text-tremor-content text-xs mb-1">
                  {scopeType === 'supplier' ? t.conteo.form.scopeOptions.supplier : t.conteo.form.scopeFrom}
                </div>
                <TextInput
                  value={scopeFrom}
                  onValueChange={setScopeFrom}
                  placeholder={scopeType === 'bin' ? 'A-01' : scopeType === 'supplier' ? 'SUP-123' : 'GON'}
                  required
                />
              </label>
            )}

            {needsTo && (
              <label className="text-sm">
                <div className="text-tremor-content text-xs mb-1">{t.conteo.form.scopeTo}</div>
                <TextInput value={scopeTo} onValueChange={setScopeTo} placeholder={scopeFrom || undefined} />
              </label>
            )}

            <label className="text-sm">
              <div className="text-tremor-content text-xs mb-1">{t.conteo.form.postMode}</div>
              <Select value={postMode} onValueChange={(v) => setPostMode(v as PostMode)} enableClear={false}>
                <SelectItem value="delta">{t.conteo.form.postModeOptions.delta}</SelectItem>
                <SelectItem value="overwrite">{t.conteo.form.postModeOptions.overwrite}</SelectItem>
              </Select>
            </label>

            <label className="text-sm">
              <div className="text-tremor-content text-xs mb-1">{t.conteo.form.uncounted}</div>
              <Select value={uncounted} onValueChange={(v) => setUncounted(v as UncountedMode)} enableClear={false}>
                <SelectItem value="skip">{t.conteo.form.uncountedOptions.skip}</SelectItem>
                <SelectItem value="zero">{t.conteo.form.uncountedOptions.zero}</SelectItem>
              </Select>
            </label>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <Switch checked={blind} onChange={setBlind} />
            {t.conteo.form.blind}
          </label>

          {err && <Text className="text-red-600 dark:text-red-400 text-sm">{err}</Text>}

          <Button type="submit" disabled={!canSubmit} loading={submitting}>
            {t.conteo.form.submit}
          </Button>
        </form>
      </Card>

      {confirmCount !== null && (
        <ConfirmDialog
          title={t.conteo.form.confirmLargeTitle}
          message={t.conteo.errors.scopeTooLarge.replace('{count}', String(confirmCount))}
          confirmLabel={t.conteo.form.confirmLarge}
          cancelLabel={t.conteo.form.cancel}
          destructive={false}
          onConfirm={() => { setConfirmCount(null); void submit(true); }}
          onCancel={() => setConfirmCount(null)}
        />
      )}
    </div>
  );
}
